/**
 * Écran d'appel de la salle d'attente — schémas partagés API ↔ Web.
 *
 * Deux publics : l'administrateur, qui déclare un écran pour un site et lui
 * donne un code d'activation ; et l'écran lui-même, qui ne lit que les
 * derniers appels de son site, sous une forme abrégée (`rules/ecran-appel.ts`).
 */
import { z } from 'zod';
/**
 * Fermeture applicative de la socket d'un écran révoqué, remplacé ou jamais
 * activé : le front ne se reconnecte pas, il retombe sur la saisie du code.
 * Partagée pour que l'API et la télé ne puissent pas diverger.
 */
export const FERMETURE_ECRAN_REVOQUE = 4003;
/**
 * Ce que la salle lit (décision 285) : `NOMS`, le nom abrégé ; `NUMEROS`
 * (`FONCTION_NUMEROS_PASSAGE`), le numéro de passage, et aucun nom ne quitte
 * le serveur.
 */
export const schemaModeEcran = z.enum(['NOMS', 'NUMEROS']);
/** Mode numéros, visite enregistrée avant l'allumage : ni nom, ni numéro (R14). */
export const TEXTE_SANS_NUMERO = '—';
/** Un appel tel que la salle le voit — jamais plus. */
export const schemaAppelAffiche = z.object({
    id: z.uuid(),
    /** « NOM P. » (`nomAffichePublic`) ou « GA1 », selon le mode. */
    texte: z.string(),
    /**
     * La spécialité en clair (« GÉNÉRALISTE »), en mode numéros seulement :
     * à côté d'un nom, elle dirait à la salle qui consulte quoi.
     */
    prestation: z.string().nullable(),
    /** Le numéro de porte du box (« Porte 2 ») : jamais sa spécialité. */
    porte: z.string(),
    /** Le dernier appel : le premier, ou un rappel du praticien. */
    appeleLe: z.iso.datetime(),
    /**
     * Quand le nom doit quitter l'écran, à l'heure du serveur. La télé le retire
     * elle-même à cette échéance, même si elle ne joint plus le serveur.
     */
    retireLe: z.iso.datetime(),
});
/**
 * Une colonne « Prochains » (décision 255) : la spécialité en titre, puis les
 * noms abrégés dans l'ordre réel d'appel. Ni identifiant, ni heure, ni motif :
 * le rang suffit, et la liste ne dit rien de plus qu'un appel.
 */
export const schemaColonneProchains = z.object({
    prestation: z.string(),
    /** « NOM P. » ou « GA1 » selon le mode, le premier appelé en tête — cinq au plus. */
    textes: z.array(z.string()),
    /** Combien attendent leur tour : la seconde colonne s'y attarde d'autant (décision 262). */
    enAttente: z.number().int().positive(),
});
export const schemaProchainsDeLaSalle = z.object({
    /**
     * Toutes les spécialités qui ont quelqu'un en attente, la plus sollicitée
     * d'abord (décision 262) : la télé en montre deux, la seconde alterne.
     */
    colonnes: z.array(schemaColonneProchains),
    /**
     * La fin de la plus proche mise de côté (R8) : le patient revient dans la
     * liste sans qu'aucun geste ne réveille la télé, elle relit à cette heure.
     */
    relireLe: z.iso.datetime().nullable(),
});
/** Ce que l'écran affiche : son site, puis les appels, le plus récent en tête. */
export const schemaAppelsDeLEcran = z.object({
    site: z.string(),
    ecran: z.string(),
    /**
     * L'heure du serveur à la lecture : la télé mesure l'écart avec sa propre
     * horloge, qui avance ou retarde, et compte les échéances à l'heure du serveur.
     */
    maintenant: z.iso.datetime(),
    /** La télé n'a pas accès à `/fonctions` : elle lit le mode ici. */
    mode: schemaModeEcran,
    /**
     * La vidéo en boucle à gauche de la télé : toujours en mode numéros, en mode
     * noms quand `FONCTION_VIDEOS_TELE` est allumée (décision 323). Absent d'une
     * vieille réponse : éteint.
     */
    videos: z.boolean().default(false),
    appels: z.array(schemaAppelAffiche),
    /**
     * Les prochains par spécialité, `null` quand `FONCTION_ECRAN_PROCHAINS` est
     * éteinte en mode noms ; toujours là en mode numéros. La voix
     * « Manaraka tompoko » suit le même état.
     */
    prochains: schemaProchainsDeLaSalle.nullable(),
    /**
     * L'adresse du son joué après le carillon (décision 258), `null` interrupteur
     * éteint. En mode numéros, le repli quand le numéro ne peut pas être dit. Elle change quand la Direction change le son : la télé prend le
     * nouveau à sa prochaine lecture, sans rechargement.
     */
    voix: z.string().nullable(),
});
/** Un écran, vu depuis l'administration. */
export const schemaEcranAppel = z.object({
    id: z.uuid(),
    siteId: z.uuid(),
    nom: z.string(),
    /** `true` quand un code attend d'être saisi sur la télé. */
    codeEnAttente: z.boolean(),
    /**
     * Une télé tient le secret de cet écran et peut afficher. Faux après un
     * nouveau code, même si l'écran avait déjà été activé.
     */
    appaire: z.boolean(),
    activeLe: z.iso.datetime().nullable(),
    /** Dernier signe de vie de la télé : une lecture, ou la socket qui répond. */
    derniereVueLe: z.iso.datetime().nullable(),
    /** L'adresse d'où la télé s'est montrée en dernier : une télé qui change de réseau se voit. */
    derniereAdresse: z.string().nullable(),
    revoqueLe: z.iso.datetime().nullable(),
    creeLe: z.iso.datetime(),
});
export const schemaCreationEcranAppel = z.object({
    nom: z.string().trim().min(1, 'Donnez un nom à l’écran').max(60),
});
/** Rendu une seule fois, à la création ou au renouvellement du code. */
export const schemaCodeActivationEcran = z.object({
    ecran: schemaEcranAppel,
    code: z.string(),
    expireLe: z.iso.datetime(),
});
/** Ce que la télé envoie pour s'activer. */
export const schemaActivationEcran = z.object({
    code: z.string().trim().min(1).max(20),
});
/** Le son de l'annonce, vu depuis Configuration (décision 258). */
export const schemaSonAnnonce = z.object({
    /** `true` : l'enregistrement d'origine ; `false` : un fichier déposé par la Direction. */
    origine: z.boolean(),
    type: z.string(),
    modifieLe: z.iso.datetime().nullable(),
});
//# sourceMappingURL=ecran-appel.js.map